import React, { useEffect, useState } from "react";

import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";

import { fetchRegisteredUsers } from "../../redux/buuserdashboard/registeredusers";
import { fetchTestsForMedicalUser } from "../../redux/apis/lab";
import { updateSingleTest } from "../../redux/apis/lab";

import SidebarWithUsers from "./buserheadepage/sidebar";
import DashboardHeader from "./buserheadepage/DashboardHeader";

import { updateTestRecommendation } from "../../redux/slices/appointmentsSlice";
import uploadLabFiles from "../../redux/apis/uploadLabFiles";
import updateTestStatus from "../../redux/apis/updateTestStatus";

import { getBusinessUserProfileImageUrl } from "../utils/buserimageUtils";

const OtherMedicalServices = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const baseUrl = process.env.REACT_APP_BACKEND_BASE_URL;

  const { registeredUsers, loading, error } = useSelector(
    (state) => state.users,
  );

  const labTests = useSelector((state) => state.labTests?.tests || []);

  const {
    user,
    loading: authLoading,
    isAuthenticated,
    userId,
  } = useSelector((state) => state.auth);

  const loggedInUserId = user?._id || userId;

  const [category, setCategory] = useState("All");
  const [selectedUser, setSelectedUser] = useState(null);
  const [Doctor, setDoctor] = useState(null);
  const [test, setTest] = useState(null);
  const [files, setFiles] = useState({});
  const [previews, setPreviews] = useState({});
  const [description, setDescription] = useState({});
  const [expandedDoctors, setExpandedDoctors] = useState({});
  const [selectedStatus, setSelectedStatus] = useState("pending");
  const [statusChanges, setStatusChanges] = useState({});
  const [updatePayload, setUpdatePayload] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");

  const today = new Date().toISOString().split("T")[0];

  const uniquePatients = Object.values(
    labTests.reduce((acc, test) => {
      const email = test?.patientInfo?.email;

      if (email && !acc[email]) {
        acc[email] = { ...test.patientInfo };
      }

      return acc;
    }, {}),
  );

  const totalPatients = uniquePatients.length;

  const activeTests = labTests.filter((t) => t.status === "Active").length;

  const pendingTests = labTests.filter((t) => t.status === "Pending").length;

  const todaysAppointments = labTests.filter(
    (t) => t?.date?.split("T")[0] === today,
  ).length;

  useEffect(() => {
    if (!authLoading && !isAuthenticated) {
      navigate("/login");
    }
  }, [authLoading, isAuthenticated, navigate]);

  useEffect(() => {
    dispatch(fetchRegisteredUsers(category)).then((res) => {
      if (res.meta.requestStatus === "fulfilled") {
        console.log("✅ Registered Users:");
      } else {
        console.log("❌ Fetch Failed:", res.payload);
      }
    });
  }, [category, dispatch]);

  const BASE_URL = process.env.REACT_APP_BACKEND_BASE_URL;

  const buildFileUrl = (rawUrl) => {
    if (!rawUrl) return "";

    if (rawUrl.startsWith("http")) return rawUrl;

    return `${BASE_URL}${rawUrl.startsWith("/") ? "" : "/"}${rawUrl}`;
  };

  useEffect(() => {
    if (userId) {
      dispatch(fetchTestsForMedicalUser(userId));
    }
  }, [userId, dispatch]);

  useEffect(() => {
    if (test?.status) {
      setSelectedStatus(test.status);
    }
  }, [test]);

  const toggleDoctorGroup = (doctorName) => {
    setExpandedDoctors((prev) => ({
      ...prev,
      [doctorName]: !prev[doctorName],
    }));
  };

  const handleFileChange = (e, testId) => {
    const selectedFiles = Array.from(e.target.files);

    const newPreviews = selectedFiles.map((file) => URL.createObjectURL(file));

    setPreviews((prev) => ({
      ...prev,
      [testId]: [...(prev[testId] || []), ...newPreviews],
    }));

    setFiles((prev) => ({
      ...prev,
      [testId]: [...(prev[testId] || []), ...selectedFiles],
    }));
  };

  const validUsers = registeredUsers;

  const filteredUsers = Array.isArray(registeredUsers)
    ? registeredUsers.filter((user) => {
        if (category === "All") return true;

        const categories = Array.isArray(user?.categoryPath)
          ? user.categoryPath
          : [];

        const isMatch = categories.includes(category);

        return isMatch;
      })
    : [];

  const updateTestHandler = async (testid, newStatus) => {
    const test = labTests.find((t) => t.testid === testid);

    if (!test) {
      console.error("❌ Test not found for testid:", testid);
      return;
    }

    const appointmentId = test.appointmentId;

    if (!appointmentId) {
      console.error("❌ Missing appointmentId in test:", test);
      return;
    }

    try {
      const resultAction = await dispatch(
        updateTestStatus({
          appointmentId,
          testid,
          newStatus,
        }),
      );

      if (updateTestStatus.fulfilled.match(resultAction)) {
        console.log("✅ Status updated successfully");

        dispatch(
          updateTestRecommendation({
            appointmentId,
            testid,
            updatedFields: {
              status: newStatus,
            },
          }),
        );

        dispatch(fetchTestsForMedicalUser(userId));
      } else {
        console.error("❌ Update failed:", resultAction.payload);
      }
    } catch (error) {
      console.error("❌ Thunk failed unexpectedly:", error.message || error);
    }
  };

  useEffect(() => {
    if (!updatePayload) return;

    console.log("📤 Dispatching updateTestRecommendation:", updatePayload);

    dispatch(updateTestRecommendation(updatePayload))
      .unwrap?.()
      .then((res) => {
        console.log("✅ Update recommendation success:", res);
      })
      .catch((err) => {
        console.error("❌ Update recommendation failed:", err);
      });

    setUpdatePayload(null);
  }, [updatePayload, dispatch]);

  const handleRemoveImage = (testId, indexToRemove) => {
    setPreviews((prev) => {
      const existing = prev[testId] || [];

      return {
        ...prev,
        [testId]: existing.filter((_, index) => index !== indexToRemove),
      };
    });

    setFiles((prev) => {
      const existing = prev[testId] || [];

      return {
        ...prev,
        [testId]: existing.filter((_, index) => index !== indexToRemove),
      };
    });
  };

  const handleSubmit = async (testId) => {
    const test = labTests.find((t) => t.testid === testId);

    if (!test) {
      console.error("❌ Test not found:", testId);
      return alert("Test not found.");
    }

    const appointmentId = test.appointmentId;

    const testFiles = files[testId] || [];
    const testDescription = description[testId] || "";

    if (testFiles.length === 0) {
      return alert("Please upload at least one file.");
    }

    try {
      const result = await dispatch(
        uploadLabFiles({
          appointmentId,
          testId,
          files: testFiles,
          labDescription: testDescription,
          businessName: test?.doctorInfo?.username,
          businessId: test?.doctorInfo?.id,
        }),
      );

      if (result.meta.requestStatus === "fulfilled") {
        const payload = result.payload;

        dispatch(
          updateSingleTest({
            appointmentId,
            testId,
            labFiles: payload?.labFiles || [],
            labDescription: payload?.labDescription || testDescription,
          }),
        );

        setFiles((prev) => ({
          ...prev,
          [testId]: [],
        }));

        setPreviews((prev) => ({
          ...prev,
          [testId]: [],
        }));

        setDescription((prev) => ({
          ...prev,
          [testId]: "",
        }));

        dispatch(fetchTestsForMedicalUser(userId));

        alert("✅ Upload successful");
      } else {
        console.error("❌ Upload failed:", result.payload);

        alert(result.payload || "Upload failed");
      }
    } catch (error) {
      console.error("❌ Upload error:", error);

      alert("Something went wrong");
    }
  };

  if (loading || authLoading) {
    return (
      <div className="flex items-center justify-center h-screen bg-slate-100">
        <div className="text-center">
          <div className="w-10 h-10 border-4 border-slate-300 border-t-sky-500 rounded-full animate-spin mx-auto mb-3" />

          <p className="text-slate-600 font-medium">Loading...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center h-screen bg-slate-100">
        <div className="bg-white border border-red-200 rounded-xl shadow-sm px-6 py-5 text-red-500">
          Error: {error}
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-100">
      {/* ========================================================= */}
      {/* FULL WIDTH HEADER                                         */}
      {/* ========================================================= */}

      <header className="fixed top-0 left-0 right-0 z-50 w-full">
        <div
          className="w-full shadow-lg"
          style={{
            backgroundImage:
              "linear-gradient(145deg, rgb(15, 31, 51) 0%, rgb(26, 58, 92) 50%, rgb(30, 73, 118) 100%)",
          }}
        >
          <DashboardHeader />
        </div>
      </header>

      {/* ========================================================= */}
      {/* BELOW HEADER AREA                                         */}
      {/* ========================================================= */}

      <div className="flex min-h-screen pt-16">
        {/* ======================================================= */}
        {/* SIDEBAR                                                  */}
        {/* ======================================================= */}

        <aside
          className="
            fixed
            left-0
            top-16
            bottom-0
            z-40
            w-64
            overflow-y-auto
            border-r
            border-slate-700/40
            shadow-xl
            hidden
            md:block
          "
          style={{
            backgroundImage:
              "linear-gradient(145deg, rgb(15, 31, 51) 0%, rgb(26, 58, 92) 50%, rgb(30, 73, 118) 100%)",
          }}
        >
          <SidebarWithUsers
            category={category}
            setCategory={(cat) => {
              setCategory(cat);
              setSelectedUser(null);
              setTest(null);
            }}
            registeredUsers={registeredUsers}
            onUserSelect={(user) => {
              if (category === "Appointments" || category === "Patients") {
                setSelectedUser(user);
                setTest(null);
              } else {
                setSelectedUser(null);
                setTest(null);
              }
            }}
          />
        </aside>

        {/* ======================================================= */}
        {/* MAIN CONTENT                                             */}
        {/* ======================================================= */}

        <div className="flex-1 md:ml-64 min-w-0">
          <main className="pt-24 px-4 pb-4 sm:px-6 sm:pb-6 lg:px-8 lg:pb-8 overflow-y-auto space-y-8">
            {/* =================================================== */}
            {/* APPOINTMENTS                                         */}
            {/* =================================================== */}

            {category === "Appointments" && (
              <>
                {labTests?.length > 0 ? (
                  <section className="border border-slate-200 rounded-2xl bg-white shadow-sm overflow-hidden">
                    <div
                      className="px-5 sm:px-6 py-5 text-white"
                      style={{
                        backgroundImage:
                          "linear-gradient(145deg, rgb(15, 31, 51) 0%, rgb(26, 58, 92) 50%, rgb(30, 73, 118) 100%)",
                      }}
                    >
                      <h2 className="text-xl sm:text-2xl font-bold">
                        Assigned Lab Tests
                      </h2>

                      <p className="text-sm text-slate-300 mt-1">
                        Manage assigned tests and upload reports
                      </p>
                    </div>

                    <div className="p-4 sm:p-6">
                      {Object.entries(
                        labTests
                          .filter((test) => {
                            const medicalUserId = userId?.toString();

                            const testUserId =
                              test?.OtherMedicalUser?._id?.toString();

                            return (
                              !test?.isCancelled &&
                              testUserId &&
                              medicalUserId === testUserId
                            );
                          })
                          .reduce((groups, test) => {
                            const doctorName =
                              test?.doctorInfo?.name || "Unknown Doctor";

                            if (!groups[doctorName]) {
                              groups[doctorName] = [];
                            }

                            groups[doctorName].push(test);

                            return groups;
                          }, {}),
                      ).map(([doctorName, tests]) => {
                        const isExpanded = expandedDoctors[doctorName];

                        const getTime = (t) => {
                          const datePart = t?.date
                            ? new Date(t.date).getTime()
                            : 0;

                          const timePart = t?.time || "00:00";

                          const [h = 0, m = 0] = timePart
                            .split(":")
                            .map(Number);

                          const final = new Date(datePart);

                          final.setHours(h, m, 0, 0);

                          return final.getTime();
                        };

                        const sortedTests = [...tests].sort(
                          (a, b) => getTime(a) - getTime(b),
                        );

                        return (
                          <div
                            key={doctorName}
                            className="mb-8 border border-slate-200 rounded-xl bg-white shadow-sm overflow-hidden"
                          >
                            {/* DOCTOR HEADER */}

                            <div
                              className="
                                  flex
                                  flex-col
                                  lg:flex-row
                                  lg:items-center
                                  gap-3
                                  lg:gap-6
                                  px-4
                                  sm:px-6
                                  py-4
                                  cursor-pointer
                                  border-b
                                  border-slate-200
                                "
                              style={{
                                background:
                                  "linear-gradient(135deg, rgba(14,165,233,0.10), rgba(30,73,118,0.10))",
                              }}
                              onClick={() => toggleDoctorGroup(doctorName)}
                            >
                              <h3 className="text-lg sm:text-xl font-bold text-slate-800 flex-1">
                                🩺 Doctor:{" "}
                                <span className="text-sky-700">
                                  {doctorName}
                                </span>{" "}
                                <span className="text-slate-500 text-sm">
                                  ({sortedTests.length})
                                </span>
                              </h3>

                              <span className="text-sm text-slate-600">
                                📅{" "}
                                {(() => {
                                  const latestTest = sortedTests
                                    .filter((t) => t?.date)
                                    .sort(
                                      (a, b) =>
                                        new Date(b.date) - new Date(a.date),
                                    )[0];

                                  if (!latestTest?.date) return "No date";

                                  return new Date(
                                    latestTest.date,
                                  ).toLocaleDateString("en-IN", {
                                    day: "2-digit",
                                    month: "short",
                                    year: "numeric",
                                  });
                                })()}
                              </span>

                              <span className="text-sm">
                                {(() => {
                                  const counts = sortedTests.reduce(
                                    (acc, t) => {
                                      const status = (
                                        t?.status || "unknown"
                                      ).toLowerCase();

                                      acc[status] = (acc[status] || 0) + 1;

                                      return acc;
                                    },
                                    {},
                                  );

                                  return (
                                    <span className="flex gap-2 flex-wrap">
                                      {counts.completed && (
                                        <span className="px-2 py-1 rounded-full bg-green-100 text-green-700 text-xs font-semibold">
                                          ✅ {counts.completed} Completed
                                        </span>
                                      )}

                                      {counts.pending && (
                                        <span className="px-2 py-1 rounded-full bg-yellow-100 text-yellow-700 text-xs font-semibold">
                                          ⏳ {counts.pending} Pending
                                        </span>
                                      )}

                                      {counts["in-progress"] && (
                                        <span className="px-2 py-1 rounded-full bg-sky-100 text-sky-700 text-xs font-semibold">
                                          🔄 {counts["in-progress"]} In Progress
                                        </span>
                                      )}

                                      {counts.confirmed && (
                                        <span className="px-2 py-1 rounded-full bg-purple-100 text-purple-700 text-xs font-semibold">
                                          ✔ {counts.confirmed} Confirmed
                                        </span>
                                      )}

                                      {counts.declined && (
                                        <span className="px-2 py-1 rounded-full bg-red-100 text-red-700 text-xs font-semibold">
                                          ❌ {counts.declined} Declined
                                        </span>
                                      )}
                                    </span>
                                  );
                                })()}
                              </span>

                              <span className="text-sky-600 text-2xl font-bold w-8 text-center">
                                {isExpanded ? "−" : "+"}
                              </span>
                            </div>

                            {/* TEST LIST */}

                            {isExpanded && (
                              <ul className="space-y-8 p-4 sm:p-6">
                                {sortedTests.map((test, i) => {
                                  const testId = test.testid || `test-${i}`;

                                  const uploadedCount =
                                    test.labFiles?.length || 0;

                                  const isCompleted =
                                    String(test.status || "")
                                      .toLowerCase()
                                      .trim() === "completed";

                                  return (
                                    <li
                                      key={testId}
                                      className={`border border-slate-200 rounded-xl overflow-hidden bg-white shadow-sm ${
                                        isCompleted ? "opacity-80" : ""
                                      }`}
                                    >
                                      {/* TEST TOP BAR */}

                                      <div className="flex justify-between items-center px-4 sm:px-5 py-3 bg-slate-50 border-b border-slate-200">
                                        <div className="bg-yellow-300 text-yellow-900 px-4 py-1.5 rounded-full text-sm font-bold">
                                          🧾 Test #{i + 1}
                                        </div>

                                        {isCompleted && (
                                          <span className="text-xs bg-green-600 text-white px-3 py-1 rounded-full font-semibold">
                                            COMPLETED
                                          </span>
                                        )}
                                      </div>

                                      <div className="p-4 sm:p-5">
                                        {/* STATUS */}

                                        <div className="flex flex-wrap items-center gap-3 mb-5">
                                          <label className="text-sm font-semibold text-slate-700">
                                            Status:
                                          </label>

                                          <select
                                            disabled={isCompleted}
                                            value={
                                              statusChanges[testId] ??
                                              test.status ??
                                              "pending"
                                            }
                                            onChange={(e) =>
                                              setStatusChanges((prev) => ({
                                                ...prev,
                                                [testId]: e.target.value,
                                              }))
                                            }
                                            className="border border-slate-300 px-3 py-2 rounded-lg bg-white text-sm focus:outline-none focus:ring-2 focus:ring-sky-400"
                                          >
                                            <option value="pending">
                                              Pending
                                            </option>

                                            <option value="confirmed">
                                              Confirmed
                                            </option>

                                            <option value="in-progress">
                                              In Progress
                                            </option>

                                            <option value="completed">
                                              Completed
                                            </option>

                                            <option value="declined">
                                              Declined
                                            </option>
                                          </select>

                                          <button
                                            type="button"
                                            disabled={
                                              isCompleted ||
                                              !statusChanges[testId] ||
                                              statusChanges[testId] ===
                                                test.status
                                            }
                                            onClick={() => {
                                              const newStatus =
                                                statusChanges[testId];

                                              if (
                                                !newStatus ||
                                                newStatus === test.status
                                              )
                                                return;

                                              updateTestHandler(
                                                test.testid,
                                                newStatus,
                                              );
                                            }}
                                            className="text-white px-5 py-2 rounded-lg text-sm font-semibold shadow-sm transition-all hover:shadow-md disabled:bg-gray-400 disabled:cursor-not-allowed"
                                            style={{
                                              backgroundImage:
                                                "linear-gradient(135deg, rgb(26, 58, 92), rgb(14, 165, 233))",
                                            }}
                                          >
                                            Save
                                          </button>
                                        </div>

                                        {/* INFO GRID */}

                                        <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-5">
                                          <div className="p-4 rounded-lg bg-slate-50 border border-slate-200">
                                            <h4 className="font-bold text-slate-700 mb-2">
                                              👤 Patient
                                            </h4>

                                            <p className="text-sm text-slate-600">
                                              <strong>Name:</strong>{" "}
                                              {test?.patientInfo?.name}
                                            </p>

                                            <p className="text-sm text-slate-600">
                                              <strong>Email:</strong>{" "}
                                              {test?.patientInfo?.email}
                                            </p>

                                            <p className="text-sm text-slate-600">
                                              <strong>Phone:</strong>{" "}
                                              {test?.patientInfo?.phone ||
                                                "Not available"}
                                            </p>
                                          </div>

                                          <div className="p-4 rounded-lg bg-slate-50 border border-slate-200">
                                            <h4 className="font-bold text-slate-700 mb-2">
                                              🩺 Doctor
                                            </h4>

                                            <p className="text-sm text-slate-600">
                                              <strong>Name:</strong>{" "}
                                              {test?.doctorInfo?.name}
                                            </p>

                                            <p className="text-sm text-slate-600">
                                              <strong>Email:</strong>{" "}
                                              {test?.doctorInfo?.email}
                                            </p>

                                            <p className="text-sm text-slate-600">
                                              <strong>Phone:</strong>{" "}
                                              {test?.doctorInfo?.phone || "—"}
                                            </p>
                                          </div>
                                        </div>

                                        {/* DETAILS */}

                                        <div className="bg-sky-50 border border-sky-100 p-4 rounded-xl">
                                          <p className="text-sm text-slate-700 mb-2">
                                            🧫 <strong>Type:</strong>{" "}
                                            {test.testType || "—"}
                                          </p>

                                          <p className="text-sm text-slate-700 mb-2">
                                            📝 <strong>Doctor Notes:</strong>{" "}
                                            {test.doctorDescription?.trim()
                                              ? test.doctorDescription
                                              : "No notes"}
                                          </p>

                                          <p className="text-sm text-slate-700 mb-2">
                                            🧪 <strong>Lab Notes:</strong>{" "}
                                            {(
                                              description[testId] ??
                                              test.labDescription
                                            )?.trim()
                                              ? (description[testId] ??
                                                test.labDescription)
                                              : "—"}
                                          </p>

                                          <p className="text-sm text-slate-600">
                                            📎 {uploadedCount} file
                                            {uploadedCount !== 1 ? "s" : ""}
                                          </p>
                                        </div>

                                        {/* FILES */}

                                        {test.labFiles?.length > 0 && (
                                          <div className="mt-5">
                                            <h4 className="font-semibold text-slate-700 mb-3">
                                              Uploaded Reports
                                            </h4>

                                            <div className="flex gap-4 flex-wrap">
                                              {test.labFiles.map(
                                                (file, idx) => {
                                                  const BASE =
                                                    process.env
                                                      .REACT_APP_BACKEND_BASE_URL;

                                                  const rawUrl =
                                                    file?.fileUrl ||
                                                    file?.url ||
                                                    "";

                                                  let clean = rawUrl.replace(
                                                    /\\/g,
                                                    "/",
                                                  );

                                                  clean = clean.replace(
                                                    "http://localhost:5000",
                                                    "",
                                                  );

                                                  if (
                                                    clean &&
                                                    !clean.startsWith("/")
                                                  ) {
                                                    clean = `/${clean}`;
                                                  }

                                                  const url = rawUrl.startsWith(
                                                    "http",
                                                  )
                                                    ? rawUrl.includes("/api/")
                                                      ? rawUrl
                                                      : rawUrl.replace(
                                                          "http://localhost:5000",
                                                          BASE,
                                                        )
                                                    : `${BASE}${clean}`;

                                                  const fileName = url
                                                    ? url.split("/").pop()
                                                    : "file";

                                                  const isImage =
                                                    file?.fileType?.startsWith(
                                                      "image",
                                                    ) ||
                                                    /\.(jpg|jpeg|png|webp|gif)$/i.test(
                                                      fileName,
                                                    );

                                                  return (
                                                    <div
                                                      key={
                                                        file?.id || url || idx
                                                      }
                                                      className="flex flex-col items-center gap-2 border border-slate-200 p-2 rounded-xl bg-white w-28 shadow-sm"
                                                    >
                                                      {isImage ? (
                                                        <a
                                                          href={url}
                                                          target="_blank"
                                                          rel="noreferrer"
                                                        >
                                                          <img
                                                            src={url}
                                                            alt="lab"
                                                            className="w-24 h-24 object-cover border rounded-lg cursor-pointer"
                                                            onError={(e) => {
                                                              console.log(
                                                                "❌ Image failed:",
                                                                url,
                                                              );

                                                              e.currentTarget.src =
                                                                "/default-file.png";
                                                            }}
                                                          />
                                                        </a>
                                                      ) : (
                                                        <div className="w-24 h-24 flex items-center justify-center bg-slate-100 border rounded-lg text-xs text-center text-slate-500">
                                                          📄 File
                                                        </div>
                                                      )}

                                                      <p className="text-[10px] text-gray-600 truncate w-full text-center">
                                                        {fileName}
                                                      </p>

                                                      <div className="flex flex-col gap-1 w-full">
                                                        <a
                                                          href={url}
                                                          target="_blank"
                                                          rel="noreferrer"
                                                          className="text-[11px] text-sky-600 underline text-center"
                                                        >
                                                          View
                                                        </a>

                                                        <a
                                                          href={url}
                                                          download={fileName}
                                                          className="text-white text-[11px] px-2 py-1 rounded text-center"
                                                          style={{
                                                            backgroundImage:
                                                              "linear-gradient(135deg, rgb(26, 58, 92), rgb(14, 165, 233))",
                                                          }}
                                                        >
                                                          Download
                                                        </a>
                                                      </div>
                                                    </div>
                                                  );
                                                },
                                              )}
                                            </div>
                                          </div>
                                        )}

                                        {/* UPLOAD */}

                                        <div className="mt-5 p-4 sm:p-5 border border-slate-200 rounded-xl bg-slate-50">
                                          {isCompleted ? (
                                            <div className="flex items-center gap-2 text-green-600 font-semibold">
                                              <span>✅</span>
                                              Completed & Locked
                                            </div>
                                          ) : (
                                            <>
                                              <input
                                                type="file"
                                                multiple
                                                accept="image/*,application/pdf"
                                                onChange={(e) =>
                                                  handleFileChange(e, testId)
                                                }
                                                className="mb-4 block w-full text-sm text-slate-600 file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:text-sm file:font-semibold file:text-white file:bg-slate-700 hover:file:bg-slate-800"
                                              />

                                              {files[testId]?.length > 0 && (
                                                <ul className="mb-4 space-y-2">
                                                  {files[testId].map(
                                                    (file, index) => (
                                                      <li
                                                        key={index}
                                                        className="flex justify-between items-center gap-3 text-sm bg-white border border-slate-200 rounded-lg px-3 py-2"
                                                      >
                                                        <span className="truncate">
                                                          📄 {file.name}
                                                        </span>

                                                        <button
                                                          type="button"
                                                          onClick={() =>
                                                            handleRemoveImage(
                                                              testId,
                                                              index,
                                                            )
                                                          }
                                                          className="text-red-500 hover:text-red-700 font-medium"
                                                        >
                                                          Remove
                                                        </button>
                                                      </li>
                                                    ),
                                                  )}
                                                </ul>
                                              )}

                                              {previews[testId]?.length > 0 && (
                                                <div className="flex gap-3 flex-wrap mb-4">
                                                  {previews[testId].map(
                                                    (src, idx) => (
                                                      <img
                                                        key={idx}
                                                        src={src}
                                                        alt="preview"
                                                        className="w-20 h-20 object-cover border border-slate-200 rounded-lg"
                                                      />
                                                    ),
                                                  )}
                                                </div>
                                              )}

                                              <textarea
                                                name={`labDescription-${testId}`}
                                                placeholder="Enter description..."
                                                value={
                                                  description[testId] ?? ""
                                                }
                                                onChange={(e) => {
                                                  const value = e.target.value;

                                                  setDescription((prev) => ({
                                                    ...prev,
                                                    [testId]: value,
                                                  }));
                                                }}
                                                className="w-full p-3 border border-slate-300 rounded-lg mb-4 resize-none focus:outline-none focus:ring-2 focus:ring-sky-400"
                                                rows={3}
                                              />

                                              <button
                                                type="button"
                                                onClick={() =>
                                                  handleSubmit(testId)
                                                }
                                                className="text-white px-5 py-2.5 rounded-lg font-semibold shadow-sm hover:shadow-md transition-all"
                                                style={{
                                                  backgroundImage:
                                                    "linear-gradient(135deg, rgb(26, 58, 92), rgb(14, 165, 233))",
                                                }}
                                              >
                                                Upload / Update Report
                                              </button>
                                            </>
                                          )}
                                        </div>
                                      </div>
                                    </li>
                                  );
                                })}
                              </ul>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  </section>
                ) : (
                  <div className="bg-white border border-slate-200 rounded-2xl p-10 text-center shadow-sm">
                    <div className="text-4xl mb-3">🧪</div>

                    <p className="text-slate-500">
                      No lab tests assigned to you.
                    </p>
                  </div>
                )}
              </>
            )}

            {/* =================================================== */}
            {/* DOCTORS / ALL / OTHER MEDICAL SERVICES              */}
            {/* =================================================== */}

            {["Doctors", "All", "Other Medical Services"].includes(
              category,
            ) && (
              <section className="border border-slate-200 rounded-2xl p-5 sm:p-6 bg-white shadow-sm">
                <div
                  className="rounded-xl p-5 mb-6 text-white"
                  style={{
                    backgroundImage:
                      "linear-gradient(145deg, rgb(15, 31, 51) 0%, rgb(26, 58, 92) 50%, rgb(30, 73, 118) 100%)",
                  }}
                >
                  <h2 className="text-xl sm:text-2xl font-bold">
                    {category} List
                  </h2>
                </div>

                <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-5">
                  {filteredUsers.map((user) => {
                    const loggedId = String(loggedInUserId || "");

                    const userId = String(user?._id || "");

                    const isYou = userId === loggedId;

                    return (
                      <div
                        key={user._id}
                        className={`bg-white p-5 rounded-xl border shadow-sm hover:shadow-md transition-all ${
                          isYou
                            ? "border-2 border-green-500 bg-green-50"
                            : "border-slate-200"
                        }`}
                      >
                        <h3 className="text-lg font-bold text-slate-800 mb-4">
                          {user.fullName}{" "}
                          {isYou && (
                            <span className="text-green-600 text-sm">
                              🟢 (You)
                            </span>
                          )}
                        </h3>

                        <div className="flex items-start gap-4">
                          {user.profilePicUrl ? (
                            <img
                              src={user.profilePicUrl}
                              alt={user.fullName || "Profile"}
                              className="w-14 h-14 rounded-full object-cover border-2 border-slate-200 flex-shrink-0"
                              onError={(e) => {
                                console.log("❌ Image failed:");

                                e.currentTarget.style.display = "none";
                              }}
                            />
                          ) : (
                            <div className="w-14 h-14 rounded-full bg-slate-200 flex-shrink-0 flex items-center justify-center">
                              👤
                            </div>
                          )}

                          <div className="text-sm text-slate-600 space-y-1 min-w-0">
                            <p className="break-words">
                              <strong className="text-slate-800">Email:</strong>{" "}
                              {user.email}
                            </p>

                            <p>
                              <strong className="text-slate-800">Phone:</strong>{" "}
                              {user.phone}
                            </p>

                            <p>
                              <strong className="text-slate-800">
                                Profession:
                              </strong>{" "}
                              {user.profession}
                            </p>

                            <p>
                              <strong className="text-slate-800">
                                Location:
                              </strong>{" "}
                              {user.city}, {user.state}, {user.country} -{" "}
                              {user.postalCode}
                            </p>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </section>
            )}

            {/* =================================================== */}
            {/* PATIENTS                                              */}
            {/* =================================================== */}

            {category === "Patients" && (
              <section className="border border-slate-200 rounded-2xl p-5 sm:p-6 bg-white shadow-sm">
                <div
                  className="rounded-xl p-5 mb-6 text-white"
                  style={{
                    backgroundImage:
                      "linear-gradient(145deg, rgb(15, 31, 51) 0%, rgb(26, 58, 92) 50%, rgb(30, 73, 118) 100%)",
                  }}
                >
                  <h2 className="text-xl sm:text-2xl font-bold">
                    👥 All Patients
                  </h2>

                  <p className="text-sm text-slate-300 mt-1">
                    View and search patient records
                  </p>
                </div>

                <input
                  type="text"
                  placeholder="Search patients..."
                  className="mb-6 w-full px-4 py-3 border border-slate-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-sky-400"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />

                {(() => {
                  const term = searchTerm.toLowerCase().trim();

                  const reducedData = labTests.reduce((acc, test) => {
                    console.log("------ TEST ITEM ------");

                    console.log("FULL TEST:", test);

                    const patient = test.patientInfo;

                    const email = patient?.email?.trim().toLowerCase();

                    if (!email) {
                      console.log("❌ No email → skipped");

                      return acc;
                    }

                    const isValid = validUsers?.some((user) => {
                      const userEmail = user.email?.trim().toLowerCase();

                      console.log("➡️ comparing:", userEmail, "vs", email);

                      return userEmail === email;
                    });

                    console.log("✅ isValid:", isValid);

                    if (
                      !acc[email] ||
                      new Date(test.date) > new Date(acc[email].testDate)
                    ) {
                      console.log("📌 Saving patient:", email);

                      acc[email] = {
                        ...patient,
                        status: test.status,
                        testDate: test.date,
                      };
                    }

                    return acc;
                  }, {});

                  const filteredPatients = Object.values(reducedData).filter(
                    (patient) => {
                      if (!term) return true;

                      return (
                        patient?.name?.toLowerCase().includes(term) ||
                        patient?.email?.toLowerCase().includes(term)
                      );
                    },
                  );

                  if (filteredPatients.length === 0) {
                    return (
                      <p className="text-center text-red-600 py-8">
                        ⚠️ No patients found
                      </p>
                    );
                  }

                  return filteredPatients.map((patient, idx) => (
                    <div
                      key={idx}
                      className="mb-4 p-5 border border-slate-200 rounded-xl bg-slate-50 hover:bg-white hover:shadow-sm transition-all"
                    >
                      <p className="text-sm mb-2">
                        <strong>Name:</strong> {patient.name}
                      </p>

                      <p className="text-sm mb-2 break-all">
                        <strong>Email:</strong> {patient.email}
                      </p>

                      <p className="text-sm mb-2">
                        <strong>Status:</strong> {patient.status}
                      </p>

                      <p className="text-sm">
                        <strong>Date:</strong>{" "}
                        {new Date(patient.testDate).toLocaleDateString()}
                      </p>
                    </div>
                  ));
                })()}
              </section>
            )}

            {/* =================================================== */}
            {/* HOME                                                  */}
            {/* =================================================== */}

            {category === "Home" && (
              <section className="border border-slate-200 rounded-2xl p-5 sm:p-6 bg-white shadow-sm">
                <div
                  className="rounded-xl p-5 mb-6 text-white"
                  style={{
                    backgroundImage:
                      "linear-gradient(145deg, rgb(15, 31, 51) 0%, rgb(26, 58, 92) 50%, rgb(30, 73, 118) 100%)",
                  }}
                >
                  <h2 className="text-xl sm:text-2xl font-bold">
                    🏠 Home Dashboard
                  </h2>

                  <p className="text-sm text-slate-300 mt-1">
                    Overview of your medical services
                  </p>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="p-5 bg-green-50 border border-green-200 rounded-xl shadow-sm">
                    <h3 className="text-lg font-semibold text-green-800 mb-2">
                      📊 Total Patients
                    </h3>

                    <p className="text-3xl font-bold text-green-700">
                      {totalPatients}
                    </p>
                  </div>

                  <div className="p-5 bg-purple-50 border border-purple-200 rounded-xl shadow-sm">
                    <h3 className="text-lg font-semibold text-purple-800 mb-2">
                      📅 Today's Appointments
                    </h3>

                    <p className="text-3xl font-bold text-purple-700">
                      {todaysAppointments}
                    </p>
                  </div>
                </div>
              </section>
            )}
          </main>
        </div>
      </div>
    </div>
  );
};

export default OtherMedicalServices;
